import { isEmpty } from 'lodash';

import type { App, Plugin } from 'vue';

import type  {NavigationGuardNext, RouteLocationNormalized } from "vue-router";

import type { GuardConfig } from "@/lib/tellscale/router-guard/types.ts";

import { RouterGuard } from "@/lib/tellscale/router-guard/router-guard.ts";

/**
 * Creates a Vue plugin that sets up router guard with declarative redirect rules
 *
 * @param options {GuardConfig} - Configuration object for router guard
 * @returns Vue plugin for router guard functionality
 */
export function createRouterGuard(options: GuardConfig): Plugin {
    /**
     * Installs the router guard plugin
     *
     * @param app
     */
    const install = (app: App) => {
        app.version;

        const { guardRules, router } = options;

        router.beforeEach((to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
            // Read query parameters from the actual URL to capture any updates from History API
            const urlParams = new URLSearchParams(window.location.search);
            const currentQuery = Object.fromEntries(urlParams.entries());

            // Merge query params: preserve both to.query and currentQuery, with currentQuery taking priority
            const mergedQuery = { ...to.query, ...currentQuery };

            // Only redirect if the merged query differs from to.query (avoid infinite loops)
            if(!isEmpty(currentQuery) && JSON.stringify(to.query) !== JSON.stringify(mergedQuery)) {
                return next({ ...to, query: mergedQuery });
            }

            // Preserve query parameters across redirects, preferring current URL params
            const query = isEmpty(to.query) ? from.query : to.query;

            // Create a guard instance
            const guard = new RouterGuard(to, from, next);

            // Execute configuration callback to register rules
            guardRules(guard, { to, from, query });

            // Validate rules and execute first matching redirect
            const redirect = guard.validate();

            if(redirect) {
                return next(redirect);
            }

            next();
        });
    }

    return { install };
}

export { RouterGuard } from "@/lib/tellscale/router-guard/router-guard.ts";
export { RedirectRule } from "@/lib/tellscale/router-guard/redirect-rule.ts";

export type {
    GuardConfig,
    GuardContext,
    GuardConfigCallback,
    MetaValue,
    ConditionFn,
} from "@/lib/tellscale/router-guard/types.ts";